import { isRequired } from '@/utils/CustomErrors'

import useBackend from '@/hooks/useBackend'

import config from '@/config'

const { buildURL, setup, doDelete, doGet, doPost, getBaseUrl } = useBackend()
setup('/ovpv3/public', config.microserviceBaseUrl, {
  responseSuccess: response => {
    return response.data
  },
  responseError: error => {
    return Promise.reject(error)
  },
})

export default function useOvp() {
  function createOrUpdateAdvisoryProtocol(payload) {
    const { basketId = isRequired('basketId'), submit = isRequired('submit') } = payload // query/url params must match payload

    const path = buildURL(`/baskets/${basketId}/advisory-protocol`, { submit })
    return doPost(path, { ...payload }).then(response => response.data)
  }

  function getSignatureBlob(dataURL) {
    const parts = dataURL.split(';base64,')
    const contentType = parts[0].split(':')[1]
    const raw = atob(parts[1])
    const rawLength = raw.length
    const uInt8Array = new Uint8Array(rawLength)

    for (let i = 0; i < rawLength; ++i) {
      uInt8Array[i] = raw.charCodeAt(i)
    }

    return new Blob([uInt8Array], { type: contentType })
  }

  function createOvpSignature(basketId = isRequired('basketId'), personId = isRequired('personId'), { signature }) {
    const url = buildURL(`/baskets/${basketId}/signature/${personId}`)
    const fileBlob = getSignatureBlob(signature)
    const formData = new FormData()
    const name = 'signatureFile'
    formData.append(name, fileBlob)

    return doPost(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  function createBasketForPartnerNumber({
    payload = isRequired('payload'),
    partnerNumber = isRequired('partnerNumber'),
    uid = isRequired('uid'),
  }) {
    const url = buildURL('/userregistry/baskets', { partnerNumber, uid })
    return doPost(url, payload).then(response => response.data)
  }

  function deleteAdvisoryProtocol({ basketId = isRequired('basketId') }) {
    const path = buildURL(`/baskets/${basketId}/advisory-protocol`)
    return doDelete(path).then(response => response.data)
  }

  function deleteOvpSignature(basketId = isRequired('basketId'), personId = isRequired('personId')) {
    const url = buildURL(`/baskets/${basketId}/signature/${personId}`)
    return doDelete(url)
  }

  function getAdvisoryProtocol({ basketId = isRequired('basketId') }) {
    return doGet(`/baskets/${basketId}/advisory-protocol`).then(response => response.data)
  }

  function getAdvisoryProtocolDocuments({ basketId = isRequired('basketId') }) {
    return doGet(`/baskets/${basketId}/advisory-protocol/documents`).then(response => response.data)
  }

  function getDistributionPartnerOrganizationName({
    distributionPartnerNumber = isRequired('distributionPartnerNumber'),
    contractStartDate = isRequired('contractStartDate'),
    language = isRequired('language'),
  }) {
    return doGet(`/distributionpartner/${distributionPartnerNumber}/organizationName`, {
      contractStartDate,
      language,
    }).then(response => response.data)
  }

  async function getDocument({ basketId = isRequired('basketId'), fileName = isRequired('fileName'), type, jwToken }) {
    const options = {
      responseType: 'blob',
      ...(jwToken && { headers: { 'X-Softlogin-Token': jwToken } }),
    }
    const response = await doGet(`/baskets/${basketId}/${fileName}.pdf`, { type }, false, options)
    window.open(URL.createObjectURL(response))
  }

  // @todo don't return a promise here. I don't see where building the url here should fail. Promise can't be used in computed, so it's inconvenient
  function getDocumentUrl({ basketId = isRequired('basketId') }, type = isRequired('type')) {
    const path = buildURL(`${getBaseUrl.value}/baskets/${basketId}/document.pdf`, {
      type,
    })
    return Promise.resolve(path)
  }

  function getRiskAssessment(basket) {
    return doPost('/risk-assessment', basket)
  }

  function fetchOvpSignature({ basketId = isRequired('basketId'), personId = isRequired('personId') }) {
    return doGet(`/baskets/${basketId}/signature/${personId}`, {}, false, { responseType: 'blob' })
  }

  function signOvpDocument(basketId = isRequired('basketId')) {
    return doGet(`/baskets/${basketId}/signDocument`)
  }

  async function signupOvpContest(basket = isRequired('basket')) {
    const { basketId, jwToken } = basket

    const options = {
      responseType: 'blob',
      ...(jwToken && { headers: { 'X-Softlogin-Token': jwToken } }),
    }

    const path = buildURL(`/baskets/${basketId}/contest`)
    return await doPost(path, {}, options).then(response => response.data)
  }

  return {
    createBasketForPartnerNumber,
    createOrUpdateAdvisoryProtocol,
    createOvpSignature,
    deleteAdvisoryProtocol,
    deleteOvpSignature,
    getAdvisoryProtocol,
    getAdvisoryProtocolDocuments,
    getDistributionPartnerOrganizationName,
    getDocument,
    getDocumentUrl,
    getRiskAssessment,
    fetchOvpSignature,
    signOvpDocument,
    signupOvpContest,
  }
}
